import { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import { splitIntoInsightBuckets } from "@/utils/splitIntoInsightBuckets";
import { emphasizeInsightList, emphasizeInsightMetrics } from "@/utils/emphasizeInsightMetrics";
import "./InterventionActionPlan.css";

export const stripSectionHeading = (markdown) =>
  String(markdown || "")
    .replace(/^#{1,3}\s+.+\n?/, "")
    .trim();

export const InsightMarkdownList = ({ content, fallback = [] }) => {
  const markdown = useMemo(() => {
    const body = stripSectionHeading(content);
    if (body) return emphasizeInsightMetrics(body);
    const list = emphasizeInsightList(fallback);
    return list || "";
  }, [content, fallback]);

  if (!markdown) return null;

  return (
    <div className="iap-detail-body iap-insight-md">
      <ReactMarkdown>{markdown}</ReactMarkdown>
    </div>
  );
};

const InterventionActionPlan = ({
  aiContent = "",
  title = "Intervention Action Plan",
  subtitle = "Actionable next steps for officials",
  showCreateTasks = true,
  defaultRoot = [],
  defaultRecs = [],
  defaultActions = [],
  className = "",
}) => {
  const buckets = useMemo(() => splitIntoInsightBuckets(aiContent), [aiContent]);

  return (
    <article className={`iap-card ${className}`.trim()} data-testid="intervention-action-plan">
      <div className="iap-card-head">
        <div>
          <div className="iap-card-title">
            <svg className="iap-small-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 11l2 2 4-4" />
              <path d="M5 4h14v16H5z" />
              <path d="M9 2h6v4H9z" />
            </svg>
            {title}
          </div>
          <div className="iap-card-subtitle">{subtitle}</div>
        </div>
        {showCreateTasks ? (
          <button type="button" className="iap-btn iap-btn-primary">
            Create Tasks
          </button>
        ) : null}
      </div>
      <div className="iap-card-body">
        <details className="iap-details" open>
          <summary>Root Cause Signals</summary>
          <InsightMarkdownList content={buckets.root} fallback={defaultRoot} />
        </details>
        <details className="iap-details">
          <summary>Recommended Interventions</summary>
          <InsightMarkdownList content={buckets.recs} fallback={defaultRecs} />
        </details>
        <details className="iap-details">
          <summary>Priority Action Items</summary>
          <InsightMarkdownList content={buckets.actions} fallback={defaultActions} />
        </details>
      </div>
    </article>
  );
};

export default InterventionActionPlan;
